import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ConfigSettings } from 'environments/ConfigSettings';

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private readonly http = inject(HttpClient);

  // Private writable signal for internal state management
  private readonly _config = signal<ConfigSettings | null>(null);

  // Public readonly signal - preferred API for new code
  readonly config = this._config.asReadonly();

  /**
   * @deprecated Use `config()` signal instead. This observable is provided
   * for backwards compatibility during migration and will be removed in a future version.
   *
   * Migration example:
   * ```typescript
   * // Before (observable):
   * this.configService.Config.subscribe(config => { ... });
   *
   * // After (signal in component):
   * config = inject(ConfigService).config;
   * // Use in template: @if (config()) { ... }
   * // Or use computed: apiUri = computed(() => this.config()?.apiUri ?? '');
   * ```
   */
  readonly Config = toObservable(this._config);

  async loadConfig(force: boolean = false): Promise<void> {
    if (!force && this._config()?.apiUri) {
      console.debug('Config already loaded');
      return;
    }
    // when local, we call back to a static file.
    // the folder/file are not included in the staging and production builds
    const result = await lastValueFrom(this.http.get<ConfigSettings>('/api/config'));
    console.debug('Config loaded:', result);
    this._config.set(result);
  }
}
